/*
 * Copyright (c) 2021, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { useState, createRef, useEffect } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useHistory, useLocation } from 'react-router-dom';
import {
  fidpAnonymous,
  fidpEmail,
  fidpGitHub,
  fidpGoogle,
  fidpEnterprise,
  fidpMicrosoft,
  googleCaptchaSiteKey,
  disableGoogleCaptcha,
  fidpE2EUser,
} from 'config';
import { isChrome, isFirefox } from 'utils/browser';
import LoginCard from 'modules/login/LoginCard';
import { getGrowthHackingParams } from 'utils/growthHacking/utm';
import { getVSCodePluginParams } from 'utils/vsCodePlugin/utm';
import PreLoader from 'pages/preloader/PreLoader';
import logger from 'utils/logger';
import { ChoreoSignInConfig, VSCodeChoreoSignInConfig } from 'utils/auth';
import { getVSCodeAuthRequestURL } from 'hooks/auth';
import AuthMainLayout from 'components/ChoreoSystem/Authenticate/AuthMainLayout/AuthMainLayout';
import { useAuthContext } from '@asgardeo/auth-react';
import generateUUID from 'utils/uuid';
import { AUTH_METHOD_BASIC } from './constants';

interface LocationState {
  returnToUrl: string;
  returnToOrg: string;
  confirmationKey: string;
  confirmationOrg: string;
  displayOrgName: string;
}

function Login() {
  const history = useHistory();
  const { state, search } = useLocation<LocationState>();
  const returnToOrg = state?.returnToOrg || '';
  const confirmationOrg = state?.confirmationOrg || '';
  const confirmationKey = state?.confirmationKey || '';
  const displayOrgName = state?.displayOrgName || '';
  const recaptchaRef = createRef<ReCAPTCHA>();
  const isSupportedEngine = isChrome() || isFirefox();
  const [isLoading, setIsLoading] = useState(false);
  const [queryParams, setQueryParams] = useState(new URLSearchParams(search));
  const isInvitation = !!confirmationOrg && !!confirmationKey;
  const fidpQueryParam = queryParams.get('fidp');
  const loginMethodQueryParam = queryParams.get('method');
  const { signIn } = useAuthContext();
  const [isMobile, setIsMobile] = useState(false);
  const returnToUrl =
    state?.returnToUrl || queryParams.get('returnToUrl') || '/';

  const handleSignIn = async (
    fidp: string,
    stateParam: { [key: string]: string },
    captchaInfo?: string,
    username?: string
  ) => {
    let encodedStateParam = '';
    let modifiedStateParams = { ...stateParam };
    // For more info https://github.com/wso2-enterprise/choreo/issues/12584
    // Looking for utm_* & visitor_id
    const queryFromReturnURL = new URL(window.location.origin + returnToUrl);
    const growthHackingParams = getGrowthHackingParams([
      ...queryParams,
      ...queryFromReturnURL.searchParams,
    ]);

    const vsCodePluginParams = getVSCodePluginParams([...queryParams]);

    const hasVsCodePluginParams: boolean =
      vsCodePluginParams.profile === 'vs-code' &&
      !!vsCodePluginParams.client_id &&
      !!vsCodePluginParams.state &&
      !!vsCodePluginParams.code_challenge;

    const hasGrowthHackingParams = growthHackingParams.length > 0;
    if (hasGrowthHackingParams) {
      growthHackingParams.forEach(([key, value]) => {
        modifiedStateParams[key] = value;
      });
    }
    if (Object.keys(modifiedStateParams).length > 0) {
      if (isInvitation) {
        modifiedStateParams = {
          ...stateParam,
          confirmationOrg,
          confirmationKey,
        };
      }
      encodedStateParam = btoa(JSON.stringify(modifiedStateParams));
    }
    try {
      setIsLoading(true);
      const config: ChoreoSignInConfig = {
        fidp,
        state: encodedStateParam,
      };
      if (username) {
        config.username = username;
      }
      if (captchaInfo) {
        config.captchaInfo = captchaInfo;
      }

      const tokenRequestConfig = {
        params: {
          tokenBindingId: generateUUID(),
        },
      };

      if (hasVsCodePluginParams) {
        const vsCodeConfig: VSCodeChoreoSignInConfig = {
          ...config,
          clientID: vsCodePluginParams.client_id,
          state: vsCodePluginParams.state,
          codeChallengeVsCode: vsCodePluginParams.code_challenge,
        };
        const authRequestURL = getVSCodeAuthRequestURL(vsCodeConfig);
        window.location.href = authRequestURL;
      } else {
        signIn(
          config,
          undefined,
          undefined,
          undefined,
          undefined,
          tokenRequestConfig
        );
      }
    } catch (signInError) {
      logger.error(signInError);
      history.push('/internal-error');
      setIsLoading(false);
    }
  };

  const handleEmailLogin = () => {
    const stateParamObj = {
      fidpId: fidpEmail,
      returnToUrl,
      returnToOrg,
    };
    handleSignIn(fidpEmail, stateParamObj);
  };

  useEffect(() => {
    const vsCodePluginParams = getVSCodePluginParams([...queryParams]);

    if (
      vsCodePluginParams.profile === 'vs-code' &&
      vsCodePluginParams.fidp === 'choreoe2etest'
    ) {
      const fidpParam = fidpE2EUser;
      const stateParamObj = {
        fidpId: fidpParam,
      };
      handleSignIn(fidpParam, stateParamObj);
    }
  }, []);

  const handleGithubLogin = () => {
    const stateParamObj = {
      fidpId: fidpGitHub,
      returnToUrl,
      returnToOrg,
    };
    handleSignIn(fidpGitHub, stateParamObj);
  };

  const handleGoogleLogin = () => {
    const stateParamObj = {
      fidpId: fidpGoogle,
      returnToUrl,
      returnToOrg,
    };
    handleSignIn(fidpGoogle, stateParamObj);
  };

  const handleMicrosoftLogin = () => {
    const stateParamObj = {
      fidpId: fidpMicrosoft,
      returnToUrl,
      returnToOrg,
    };
    handleSignIn(fidpMicrosoft, stateParamObj);
  };

  const handleEnterpriseLogin = (userEmail: string) => {
    const fidpParam = fidpEnterprise || 'EnterpriseIDP';
    const stateParamObj = {
      fidpId: fidpParam,
      returnToUrl,
      returnToOrg,
    };
    handleSignIn(fidpParam, stateParamObj, undefined, userEmail);
  };

  const handleCaptchaChange = (token: string | null) => {
    const stateParamObj = {
      fidpId: fidpAnonymous,
      returnToUrl,
      returnToOrg,
    };
    const captchaInfoParamObj: { [key: string]: string } = {};
    if (googleCaptchaSiteKey && token) {
      captchaInfoParamObj.siteKey = googleCaptchaSiteKey;
      captchaInfoParamObj.captchaToken = token;
    }
    const encodedCaptchaInfoParam = btoa(JSON.stringify(captchaInfoParamObj));
    handleSignIn(fidpAnonymous, stateParamObj, encodedCaptchaInfoParam);
  };

  const handleAnonymousLogin = () => {
    if (disableGoogleCaptcha === 'true') {
      handleCaptchaChange(null);
    } else {
      recaptchaRef.current?.execute();
    }
  };
  useEffect(() => {
    const qParams = new URLSearchParams(search);
    setQueryParams(qParams);
  }, [search]);

  useEffect(() => {
    if (loginMethodQueryParam === AUTH_METHOD_BASIC) {
      handleEmailLogin();
    }
  }, [loginMethodQueryParam]);

  useEffect(() => {
    if (fidpQueryParam) {
      if (fidpQueryParam === fidpAnonymous) {
        handleAnonymousLogin();
      } else {
        const stateParamObj = {
          returnToUrl,
        };
        handleSignIn(fidpQueryParam, stateParamObj);
      }
    }
  }, []);

  const handleResize = () => {
    if (window.innerWidth < 1000) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);
  }, []);

  if (isLoading || fidpQueryParam) {
    return (
      <>
        <PreLoader />
        {disableGoogleCaptcha === 'false' &&
          fidpQueryParam === fidpAnonymous && (
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={googleCaptchaSiteKey}
              size="invisible"
              onChange={handleCaptchaChange}
            />
          )}
      </>
    );
  }

  return (
    <AuthMainLayout isMobile={isMobile} isSupportedEngine={isSupportedEngine}>
      <LoginCard
        isInvitation={isInvitation}
        invitedOrgName={displayOrgName}
        onEmailClick={handleEmailLogin}
        onGithubClick={handleGithubLogin}
        onGoogleClick={handleGoogleLogin}
        onMicrosoftClick={handleMicrosoftLogin}
        onAnonymousClick={handleAnonymousLogin}
        onEnterpriseLoginClick={handleEnterpriseLogin}
        isMobile={isMobile}
      />

      {disableGoogleCaptcha === 'false' && (
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey={googleCaptchaSiteKey}
          size="invisible"
          onChange={handleCaptchaChange}
        />
      )}
    </AuthMainLayout>
  );
}

export default Login;
